import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/',
      children: [
        {
          path: 'auth/',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'auth/login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'auth/register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'auth/forget-password', element: <ForgetPassword /> },
        {
          path: 'auth/reset-password/:email/:token',
          element: <ResetPassword />,
        },
        { path: 'auth/verify/phone', element: <VerifyCode /> },
        { path: 'auth/verify/email/:token', element: <VerifyEmail /> },
      ],
    },
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        { element: <Dashboard />, index: true },
        {
          path: '/orders',
          children: [
            { element: <Navigate to="/orders" replace /> },
            {
              path: '/orders/',
              element: (
                <AuthGuard>
                  <Orders />
                </AuthGuard>
              ),
            },
            {
              path: '/orders/:slug/',
              element: (
                <AuthGuard>
                  <OrderStatus />
                </AuthGuard>
              ),
            },
          ],
        },
        {
          path: '/categories',
          children: [
            { element: <Navigate to="/categories/" replace /> },
            { path: '/categories/', element: <CategoryList /> },
            { path: '/categories/:slug', element: <CategoryList /> },
          ],
        },
        {
          path: '/services',
          children: [
            { element: <Navigate to="/services/" replace /> },
            {
              path: '/services/',
              element: <Navigate to="/categories/" replace />,
            },
            { path: '/services/:slug', element: <ServiceDetails /> },
            { path: '/services/:slug/order', element: <ServiceCheckout /> },
          ],
        },
        {
          path: '/financial',
          children: [
            { element: <Navigate to="/financial/payments" replace /> },
            {
              path: '/financial/payments',
              element: (
                <AuthGuard>
                  <FinancialPayments />
                </AuthGuard>
              ),
            },
          ],
        },

        {
          path: '/support',
          children: [
            {
              path: '/support',
              element: <HandleSupport />,
            },
          ],
        },
        {
          path: '/account',
          children: [
            {
              path: '/account',
              element: (
                <AuthGuard>
                  <Account />
                </AuthGuard>
              ),
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ForgetPassword = Loadable(
  lazy(() => import('../pages/auth/ForgetPassword'))
);
const ResetPassword = Loadable(
  lazy(() => import('../pages/auth/ResetPassword'))
);
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const VerifyEmail = Loadable(lazy(() => import('../pages/auth/VerifyEmail')));
// Dashboard

// Orders
const Orders = Loadable(lazy(() => import('../pages/orders/Index')));
const OrderStatus = Loadable(lazy(() => import('../pages/orders/OrderStatus')));

//Service
const ServiceDetails = Loadable(
  lazy(() => import('../pages/service/ServiceDetails'))
);
const ServiceCheckout = Loadable(
  lazy(() => import('../pages/service/ServiceCheckout'))
);

//Category
const CategoryList = Loadable(lazy(() => import('../pages/categories/Index')));
// Financial
const FinancialPayments = Loadable(
  lazy(() => import('../pages/financial/Payments'))
);

// Account
const Account = Loadable(lazy(() => import('../pages/UserAccount')));

// Dashboard
const Dashboard = Loadable(lazy(() => import('../pages/Dashboard')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

const HandleSupport = () => {
  window.location.href = `${window.location.origin}/support`;
};
